import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import postgresBackendService from '../services/postgresBackendService';

/**
 * useRoles — Lightweight hook to fetch user roles for sidebar display.
 * Uses getAllRoles() (no tools JOIN) for speed.
 *
 * Optimistic mutations (insertOptimistic / updateOptimistic / removeOptimistic)
 * mutate the local array without touching the backend. The Explore split layout
 * uses these from a `role_builder_update` WS listener so the grid animates
 * inserts and exits at the same moment the Builder finishes the N8N call,
 * without paying for a refetch round-trip. `refetch()` remains available as a
 * fallback for cases where the WS payload is too sparse to reconstruct the
 * card (e.g. restore of a role that fell out of the cache).
 */
export const useRoles = ({ includeDraft = false } = {}) => {
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, loading: authLoading } = useAuth();

  const fetchRoles = useCallback(async () => {
    if (authLoading) return;
    if (!user?.id) {
      setRoles([]);
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const data = await postgresBackendService.getAllRoles({ includeDraft });
      setRoles(data || []);
    } catch (err) {
      console.error('[useRoles] Error fetching roles:', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [authLoading, user?.id, includeDraft]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // Insert if absent, replace if present. Idempotent — a duplicate WS event
  // never produces two cards.
  const insertOptimistic = useCallback((role) => {
    if (!role?.id) return;
    setRoles((prev) => {
      const idx = prev.findIndex((r) => r.id === role.id);
      if (idx === -1) return [...prev, role];
      const next = prev.slice();
      next[idx] = { ...prev[idx], ...role };
      return next;
    });
  }, []);

  // Shallow-merge `changes` into the matching role. No-op if the rolId is
  // unknown (caller can fall back to refetch in that case).
  const updateOptimistic = useCallback((rolId, changes) => {
    if (!rolId || !changes) return;
    setRoles((prev) => {
      const idx = prev.findIndex((r) => r.id === rolId);
      if (idx === -1) return prev;
      const next = prev.slice();
      next[idx] = { ...prev[idx], ...changes };
      return next;
    });
  }, []);

  const removeOptimistic = useCallback((rolId) => {
    if (!rolId) return;
    setRoles((prev) => {
      const idx = prev.findIndex((r) => r.id === rolId);
      if (idx === -1) return prev;
      return prev.filter((r) => r.id !== rolId);
    });
  }, []);

  return {
    roles,
    isLoading,
    error,
    refetch: fetchRoles,
    insertOptimistic,
    updateOptimistic,
    removeOptimistic,
  };
};
